import { useState } from 'react';
import { Upload } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useImportFile, type ImportSummary } from '@/api/file-import';

interface FileImportCardProps {
  accountId: string;
}

export function FileImportCard({ accountId }: FileImportCardProps): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const { mutateAsync, isPending } = useImportFile(accountId);

  const handleImport = (): void => {
    if (!file) return;

    void (async () => {
      setParseError(null);
      setSummary(null);

      let records: unknown;
      try {
        records = JSON.parse(await file.text());
      } catch {
        setParseError('Could not parse file: invalid JSON.');
        return;
      }

      if (!Array.isArray(records)) {
        setParseError('Could not parse file: expected a JSON array of records at the top level.');
        return;
      }

      const result = await mutateAsync(records);
      setSummary(result.summary);
    })();
  };

  const nothingImported = summary !== null
    && summary.created + summary.updated + summary.unchanged === 0
    && summary.parsed > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          <Upload className="h-4 w-4" />
          File Import
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <input
            type="file"
            accept="application/json"
            data-testid="file-import-input"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); }}
          />
          <Button
            size="sm"
            data-testid="file-import-submit"
            disabled={!file || isPending}
            onClick={handleImport}
          >
            Import
          </Button>
        </div>

        {parseError && (
          <p className="text-sm text-destructive" data-testid="file-import-parse-error">
            {parseError}
          </p>
        )}

        {summary && (
          <div className="space-y-3 text-sm" data-testid="file-import-summary">
            {nothingImported && (
              <p className="font-medium text-destructive" data-testid="file-import-nothing">
                Nothing was imported.
              </p>
            )}
            <div className="grid grid-cols-3 gap-2 md:grid-cols-6">
              <div>
                <div className="text-muted-foreground">Created</div>
                <div className="text-lg font-semibold">{summary.created}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Updated</div>
                <div className="text-lg font-semibold">{summary.updated}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Unchanged</div>
                <div className="text-lg font-semibold">{summary.unchanged}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Skipped</div>
                <div className="text-lg font-semibold">{summary.skipped.length}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Rejected</div>
                <div className="text-lg font-semibold">{summary.rejected.length}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Failed</div>
                <div className="text-lg font-semibold">{summary.failed.length}</div>
              </div>
            </div>

            {summary.rejected.length > 0 && (
              <div>
                <div className="font-medium text-muted-foreground">Rejected records</div>
                <ul className="list-disc pl-5">
                  {summary.rejected.map((r) => (
                    <li key={r.id}>{r.id}: {r.reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {summary.failed.length > 0 && (
              <div>
                <div className="font-medium text-muted-foreground">Failed records</div>
                <ul className="list-disc pl-5">
                  {summary.failed.map((f) => (
                    <li key={f.id}>{f.id}: {f.reason}</li>
                  ))}
                </ul>
              </div>
            )}

            {summary.rejectedImageUrls.length > 0 && (
              <div>
                <div className="font-medium text-muted-foreground">Rejected image URLs</div>
                <ul className="list-disc pl-5">
                  {summary.rejectedImageUrls.map((img) => (
                    <li key={`${img.id}-${img.url}`}>{img.id}: {img.url}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
